import * as React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useTranslation } from 'react-i18next'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined'
import { Box, CircularProgress, Link, Typography } from '@mui/material'

import { sendInvite } from 'api/Participant/SendInvite/Action'
import { useAlert } from 'components/Shared/Alert'
import { useAppSelector } from 'redux/Hook'

type AddDialogProps = {
  isOpen: boolean
  handleCancel: () => void
}

type InputError = 'empty' | 'missing@' | 'invalid' | 'userExists' | boolean

const EMAIL_REGEX = /^[a-zA-Z0-9._+&%'-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/i

const AddParticipantsDialog = ({ isOpen, handleCancel }: AddDialogProps) => {
  const [hasError, onError] = React.useState<InputError>(false)
  const [address, setAddress] = React.useState('')
  const [sending, onSending] = React.useState(false)

  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const user = useAppSelector((state) => state.user.user)

  const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    hasError && onError(false)

    setAddress(target.value)
  }

  const handleReset = () => {
    onError(false)
    setAddress('')
    onSending(false)
  }

  const handleClose = () => {
    handleReset()
    handleCancel()
  }

  const validateUserInput = () => {
    if (!address) {
      onError('empty')
      return
    }

    if (!address.includes('@')) {
      onError('missing@')
      return
    }

    if (!EMAIL_REGEX.test(address)) {
      onError('invalid')
      return
    }

    return true
  }

  const checkAPIResponse = (response: { status: number }) => {
    const statusCode = response?.status

    if (statusCode) {
      if (statusCode === 409) onError('userExists')

      if (statusCode === 422) onError('invalid')

      onSending(false)
      return
    }

    handleClose()

    showAlert({
      type: 'success',
      title: t(`participant.inviteAdmin.successText`),
    })
  }

  const handleSubmit = async () => {
    if (validateUserInput()) {
      onSending(true)

      const result = await sendInvite(address)

      checkAPIResponse(result?.response)
    }
  }

  const getHelperText = () => {
    const helpText = t(`participant.inviteAdmin.helpText.${hasError}`)

    if (hasError) return helpText

    return ''
  }

  return (
    <Dialog
      open={isOpen}
      aria-labelledby={t(`participant.inviteAdmin.inviteOption`)}
      aria-describedby={t(`participant.inviteAdmin.descriptionText`)}
      data-testid="invite-dialog"
      PaperProps={{ sx: { minHeight: 408 } }}
    >
      <DialogTitle>
        <Typography variant="h5" component="h5" pt={3}>
          {t(`participant.inviteAdmin.inviteOption`)}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" pb={3}>
          {t(`participant.inviteAdmin.descriptionText`)}
        </Typography>

        <TextField
          label={t(`participant.inviteAdmin.addressLabel`)}
          value={address}
          onChange={handleChange}
          error={!!hasError}
          helperText={getHelperText()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineOutlinedIcon />
              </InputAdornment>
            ),
          }}
          variant="standard"
          fullWidth
          data-testid="invite-email-input"
          autoFocus
        />

        {user.roleName !== 'hub_admin' && (
          <Box mt={3}>
            <Link
              target={'_blank'}
              href="/admin/sites/settings?key=messageandnotification"
            >
              {t(`participant.inviteAdmin.customizeLinkText`)}
            </Link>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          sx={{ mr: 1 }}
          onClick={handleClose}
          data-testid="invite-email-cancel"
        >
          {t('common.cancel')}
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={sending}
          data-testid="invite-email-submit"
        >
          {sending && <CircularProgress size={20} sx={{ mr: 1 }} />}
          {t(`participant.inviteAdmin.sendBtnText`)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddParticipantsDialog
